import { RefreshCcwIcon } from "lucide-react";
import React from "react";
import { useSearchParams } from "react-router-dom";
import { useImmer } from "use-immer";

import {
  IgAlert,
  IgAnalyticsTooltip,
  IgButton,
  IgChip,
  IgIconButton,
  IgTab,
  IgTabs,
  IgTypography,
} from "@intelligems/ig-theme";
import { useTheme } from "@mui/material";
import Stack from "@mui/material/Stack";
import { AnalyticsFilterDrawerWrapper } from "@src/features/analytics/analyticsFilters/AnalyticsFilterDrawerWrapper";
import { AnalyticsDateRange } from "@src/features/analytics/analyticsHeader/AnalyticsDateRange";
import { AnalyticsDateRangeModal } from "@src/features/analytics/analyticsHeader/AnalyticsDateRangeModal";
import { FiltersButton } from "@src/features/analytics/analyticsHeader/FiltersButton";
import { useActiveFilterChips } from "@src/features/analytics/analyticsHeader/use-active-filter-chips";
import { getFilterLabel } from "@src/features/analytics/analyticsHeader/utils";
import { StatusRenderer } from "@src/features/experiments/pages/home/components/experiencesList/StatusRenderer";
import { ConfigureAnalytics } from "@src/features/sharedExperience/components/configureAnalytics/ConfigureAnalytics";
import { useDispatch, useSelector } from "@src/store";
import { getHasAnyUpsellBlocks } from "@src/store/slices/checkoutBlocks/selectors";
import {
  getExperienceAnalyticsFilters,
  refreshData,
} from "@src/store/slices/dashboardFilters";
import { isDrawerOpen } from "@src/store/slices/drawers";
import { getCustomMetrics } from "@src/store/slices/experienceMetrics/experienceMetrics";
import { selectedExperienceState } from "@src/store/slices/experiences";
import { getExperienceIcon } from "@src/utils/experienceTypeIcons";
import { useQueryClient } from "@tanstack/react-query";

import { ReadOnlyModeProvider } from "../context/ReadOnlyModeContext";
import {
  ShareTab,
  useShareAnalyticsContext,
} from "../context/ShareAnalyticsContext";

import { ShareAnalyticsDashboard } from "./ShareAnalyticsDashboard";
import { ShareLinkBanner } from "./ShareLinkBanner";
import { ShareLoadingSkeleton } from "./ShareLoadingSkeleton";
import { SharePageSelect } from "./SharePageSelect";

export const ShareDesktopView = () => {
  const MAX_FILTER_CHIPS_SHOWN = 10;
  const {
    pageName,
    availablePages,
    experienceFe,
    isLoading,
    organization,
    activeTab,
    showConfigureAnalytics,
    currencyConfig,
  } = useShareAnalyticsContext();
  const [, setSearchParams] = useSearchParams();
  const theme = useTheme();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const customMetrics = useSelector(getCustomMetrics);
  const dashboardFilters = useSelector(getExperienceAnalyticsFilters);
  const analyticsFilterDrawerOpen = useSelector(
    isDrawerOpen("analyticsFilters")
  );
  // Check if Redux has been hydrated - needed for ConfigureAnalytics tab
  const experienceState = useSelector(selectedExperienceState);
  const isReduxHydrated = experienceState.isSuccess;

  const icon = getExperienceIcon(experienceFe, 24);

  const hasUpsellBlock = useSelector(getHasAnyUpsellBlocks());
  const activeFilterArray = useActiveFilterChips({
    dashboardFilters,
    experience: experienceFe,
    timezone: organization.timezone,
    isUpsell:
      hasUpsellBlock || (experienceFe?.testTypes.hasTestOnsiteUpsell ?? false),
    storeCurrency: currencyConfig,
  });

  // Date range modal state
  const [anchorEl, setAnchorEl] = useImmer<HTMLElement | null>(null);
  const handlePopoverClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Share shows its own date-range control, so drop the start/end chips.
  // Match the authed header for the rest: hide the z-score chip and any
  // empty-array filters.
  const filteredFilters = activeFilterArray.filter((filter) => {
    if (filter.key === "startTime" || filter.key === "endTime") {
      return false;
    }
    if (filter.key === "isZScoreEnabled") {
      return false;
    }
    if (Array.isArray(filter.value) && filter.value.length === 0) {
      return false;
    }
    return true;
  });

  const handleRefresh = () => {
    dispatch(refreshData());
    void queryClient.invalidateQueries({
      predicate: (query) =>
        query.queryKey.some(
          (key) =>
            typeof key === "object" &&
            JSON.stringify(key).includes("dashboards")
        ),
    });
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: ShareTab) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      params.set("tab", newValue);
      return params;
    });
  };

  const container = {
    width: "100%",
    maxWidth: "1200px",
    alignSelf: "center",
    paddingX: 4,
  };

  return (
    <>
      <Stack spacing={0} sx={{ width: "100%" }}>
        <ShareLinkBanner />

        <Stack
          sx={{ paddingTop: 8, paddingBottom: 16, width: "100%" }}
          spacing={2}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={4}
            sx={{ ...container }}
          >
            {icon ? icon : null}
            <IgTypography variant="h2" fontWeight={600}>
              {experienceFe.name}
            </IgTypography>
            <StatusRenderer experience={experienceFe} displayVariant={"chip"} />
          </Stack>

          {/* Filter chips */}
          <Stack
            direction="row"
            flexWrap="nowrap"
            spacing={1}
            sx={{ ...container }}
          >
            {filteredFilters
              .slice(0, MAX_FILTER_CHIPS_SHOWN)
              .map((filter, index) => (
                <Stack key={filter.key || `filter-${index}`}>
                  <IgChip
                    variant="outlined"
                    size="small"
                    label={getFilterLabel(filter, customMetrics)}
                  />
                </Stack>
              ))}
            {filteredFilters.length > MAX_FILTER_CHIPS_SHOWN && (
              <Stack>
                <IgChip
                  size="small"
                  variant="outlined"
                  label={`+${filteredFilters.length - MAX_FILTER_CHIPS_SHOWN}`}
                />
              </Stack>
            )}
          </Stack>

          {/* Tabs row - only show if experiments have Configure Analytics */}
          {showConfigureAnalytics && (
            <Stack
              sx={{
                borderBottom: `1px solid ${theme.igBorders.core.default}`,
              }}
            >
              <Stack sx={{ ...container }}>
                <IgTabs value={activeTab} onChange={handleTabChange}>
                  <IgTab value="results" label="Results" />
                  <IgTab
                    value="configure-analytics"
                    label="Analytics Configurations"
                  />
                </IgTabs>
              </Stack>
            </Stack>
          )}

          {/* Sticky header */}
          <Stack
            direction="column"
            sx={{
              position: "sticky",
              top: 0,
              zIndex: theme.zIndex.appBar,
              backgroundColor: theme.igBackgroundColors.baseLightMid,
              borderBottom: `1px solid ${theme.igBorders.core.default}`,
              paddingTop: 3,
              paddingBottom: 3,
            }}
          >
            {/* Controls row - only show for Results tab */}
            {activeTab === "results" && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
              >
                <Stack
                  direction="row"
                  sx={{
                    ...container,
                  }}
                >
                  <SharePageSelect
                    currentPage={pageName}
                    availablePages={availablePages}
                  />

                  <Stack
                    flex={1}
                    direction="row"
                    justifyContent="flex-end"
                    alignItems="center"
                  >
                    {/* Controls stack */}
                    <Stack direction="row" alignItems="center" gap={2}>
                      <FiltersButton disabled={isLoading} />
                      <Stack
                        data-testid="analytics-date-range"
                        onClick={handlePopoverClick}
                        sx={{ flexShrink: 0, "&:hover": { cursor: "pointer" } }}
                      >
                        <IgButton variant="outlined">
                          <AnalyticsDateRange experience={experienceFe} />
                        </IgButton>
                      </Stack>
                      <IgAnalyticsTooltip
                        title={
                          isLoading
                            ? "Please wait for the data to return before refreshing"
                            : "Refresh Data"
                        }
                      >
                        <IgIconButton
                          disabled={isLoading}
                          onClick={handleRefresh}
                        >
                          <RefreshCcwIcon size={16} />
                        </IgIconButton>
                      </IgAnalyticsTooltip>
                    </Stack>

                    {/* Date range modal */}
                    {anchorEl && (
                      <AnalyticsDateRangeModal
                        anchorEl={anchorEl}
                        setAnchorEl={(anchor) => setAnchorEl(anchor)}
                        experience={experienceFe}
                      />
                    )}
                  </Stack>
                </Stack>
              </Stack>
            )}
          </Stack>

          <Stack
            sx={{
              ...container,
            }}
            alignItems="center"
            justifyContent="center"
          >
            {activeTab === "results" ? (
              <ReadOnlyModeProvider isReadOnly>
                <ShareAnalyticsDashboard />
              </ReadOnlyModeProvider>
            ) : activeTab === "configure-analytics" ? (
              // Wait for Redux to be hydrated before rendering ConfigureAnalytics
              // to prevent crashes from components accessing selectedExperience
              !isReduxHydrated ? (
                <ShareLoadingSkeleton />
              ) : (
                <ReadOnlyModeProvider isReadOnly>
                  <Stack spacing={2} sx={{ width: "100%" }}>
                    <IgAlert
                      alertKey="configure-analytics-view-only"
                      message={
                        <IgTypography>
                          This is a view-only share link. To make changes,
                          access the analytics dashboard directly.
                        </IgTypography>
                      }
                      severity="info"
                    />
                    <Stack sx={{ opacity: 0.5 }}>
                      <ConfigureAnalytics />
                    </Stack>
                  </Stack>
                </ReadOnlyModeProvider>
              )
            ) : null}
          </Stack>
        </Stack>
      </Stack>

      {/* Filter drawer */}
      {analyticsFilterDrawerOpen && (
        <ReadOnlyModeProvider isReadOnly>
          <AnalyticsFilterDrawerWrapper
            renderLocation="experience"
            openFilters={analyticsFilterDrawerOpen}
            pageName={pageName}
            currentExperience={experienceFe}
            allCountryCodesData={[]}
          />
        </ReadOnlyModeProvider>
      )}
    </>
  );
};
